import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { Typography, Button, Spinner } from 'components/common';
import InputPassword from 'components/form/input-password/input-password';
import AppRoutes from '@/const/routes';
import { authService } from '@/api';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { getLocalStorage } from '@/utils/localStorage';
import { passwordCriteria } from '@/const';
import { AuthLayout } from '@/components/layout/layout/AuthLayout';
import { ResetCard } from '@/components/auth/ResetCard';

export const NewPasswordScreen = (): JSX.Element => {
	const router = useRouter();
	const { pathname } = router;
	const { code } = router.query;
	const { toastError, toastSuccess } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [isValidCode, setIsValidCode] = React.useState<boolean>();
	const [isPasswordSaved, setIsPasswordSaved] = React.useState<boolean>(false);
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isDirty, isValid },
		setError,
	} = useForm({ mode: 'onChange' });

	// Inputs rules
	const rules = {
		password: {
			required: {
				value: true,
				message: 'This is required.',
			},
		},
		password_confirm: {
			required: {
				value: true,
				message: 'This is required.',
			},
			validate: (value: string) =>
				value === watch('password') ||
				'The passwords do not match. Please ensure they are identical.',
		},
	};

	React.useEffect(() => {
		if (
			watch('password_confirm') &&
			watch('password') !== watch('password_confirm')
		) {
			setError('password_confirm', {
				message:
					'The passwords do not match. Please ensure they are identical.',
			});
		}
	}, [watch('password'), watch('password_confirm')]);

	React.useEffect(() => {
		if (code) verifyCode();
	}, [code]);

	const verifyCode = async (): Promise<void> => {
		try {
			await authService.verifyRecoveryPasswordCode(code as string);
			setIsValidCode(true);
		} catch (error) {
			setIsValidCode(false);
		}
	};

	const resendCode = async (): Promise<void> => {
		try {
			const email = getLocalStorage('user_email');
			await authService.forgotPassword(email);
			toastSuccess('Password reset email has been successfully sent.');
		} catch (error: any) {
			toastError(formatErrorMessage(error.message));
		}
	};

	const handleSubmitDataForm = async (data: any): Promise<void> => {
		setIsLoading(true);
		try {
			const newData = {
				code: code as string,
				password: data.password,
				confirmPassword: data.password_confirm,
			};
			await authService.confirmNewPassword(newData);
			setIsPasswordSaved(true);

			setTimeout(() => {
				pathname.includes('/lawyerapp')
					? router.push(AppRoutes.LAWYERAPP_AUTH_SIGN_IN)
					: router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
			}, 2000);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const getTitle = () => {
		if (typeof isValidCode === 'boolean' && !isPasswordSaved) {
			return isValidCode ? (
				'Reset Your Password'
			) : (
				<span>
					Your password reset <br /> link has expired
				</span>
			);
		} else if (isPasswordSaved) {
			return 'Your password has been successfully reset.';
		}
	};

	const handleBack = () => {
		const route = pathname.includes('/lawyerapp')
			? AppRoutes.LAWYERAPP_AUTH_SIGN_IN
			: AppRoutes.CLIENTAPP_AUTH_SIGN_IN;
		router.push(route);
	};

	return (
		<AuthLayout withCarrousel={false}>
			<ResetCard
				title={getTitle()}
				handleBack={handleBack}
				classNameTitle="!text-4xl"
				srcImage={
					isPasswordSaved
						? '/img/landing/svg/black-magic-woman-alt.svg'
						: undefined
				}
			>
				<>
					{isValidCode === undefined ? (
						<div className="flex justify-center items-center w-full mt-4">
							<Spinner type="loadingPage" />
						</div>
					) : isValidCode ? (
						isPasswordSaved ? (
							<>
								<div className="flex justify-center w-full mt-10 mb-4">
									<Spinner type="loadingPage" />
								</div>
								<Typography
									type="regular-18"
									className="opacity-60 text-center"
								>
									Redirecting to the sign-in page in a few seconds
								</Typography>
							</>
						) : (
							<form
								onSubmit={handleSubmit(handleSubmitDataForm)}
								className="space-y-6 w-full"
							>
								<div>
									<InputPassword
										name="password"
										title="Password"
										validate={true}
										register={register}
										rules={rules.password}
										error={errors.password}
										className="mb-4"
									/>
									{!errors.password && (
										<Typography
											type="regular-12"
											className="text-opacity-black"
										>
											{passwordCriteria}
										</Typography>
									)}
								</div>
								<InputPassword
									name="password_confirm"
									title="Confirm Password"
									validate={false}
									register={register}
									rules={rules.password_confirm}
									error={errors.password_confirm}
									className="mt-4 3xl:mt-6"
								/>
								<Button
									type="submit"
									size="full"
									decoration="primary"
									disabled={isLoading || !isDirty || !isValid}
									className="!w-[300px] !h-16 mx-auto"
								>
									Reset Password
								</Button>
							</form>
						)
					) : (
						<>
							<Typography
								type="regular-18"
								className="text-center opacity-60 mb-6"
							>
								Please request a new one to proceed with <br /> resetting your
								password.
							</Typography>
							<Button
								size="fit"
								decoration="primary"
								disabled={isLoading}
								label="Resend Password Email"
								onClick={resendCode}
								className="mx-auto !w-[300px] !h-16"
							></Button>
						</>
					)}
				</>
			</ResetCard>
		</AuthLayout>
	);
};
